import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getExams } from "../api/client";
import { useExam } from "../context/ExamContext";
import AppSidebar from "./AppSidebar";

const ACCA_LEVELS = {
  acca_knowledge: {
    title: "Applied Knowledge",
    count: "3 Papers",
    icon: "bi-book",
    papers: [
      {
        code: "BT",
        name: "Business and Technology",
        examId: 3,
      },
      {
        code: "MA",
        name: "Management Accounting",
        examId: 9,
      },
      {
        code: "FA",
        name: "Financial Accounting",
        examId: 10,
      },
    ],
  },

  acca_skills: {
    title: "Applied Skills",
    count: "6 Papers",
    icon: "bi-journal-text",
    papers: [
      {
        code: "LW",
        name: "Corporate and Business Law",
        examId: 4,
      },
      {
        code: "PM",
        name: "Performance Management",
        examId: 11,
      },
      {
        code: "TX",
        name: "Taxation",
        examId: 12,
      },
      {
        code: "FR",
        name: "Financial Reporting",
        examId: 13,
      },
      {
        code: "AA",
        name: "Audit and Assurance",
        examId: 14,
      },
      {
        code: "FM",
        name: "Financial Management",
        examId: 15,
      },
    ],
  },

  acca_professional: {
    title: "Strategic Professional",
    count: "4 Papers",
    icon: "bi-mortarboard",
    mandatory: [
      {
        code: "SBL",
        name: "Strategic Business Leader",
        examId: 5,
      },
      {
        code: "SBR",
        name: "Strategic Business Reporting",
        examId: 16,
      },
    ],
    options: [
      {
        code: "AFM",
        name: "Advanced Financial Management",
        examId: 17,
      },
      {
        code: "APM",
        name: "Advanced Performance Management",
        examId: 18,
      },
      {
        code: "ATX",
        name: "Advanced Taxation",
        examId: 19,
      },
      {
        code: "AAA",
        name: "Advanced Audit and Assurance",
        examId: 20,
      },
    ],
  },
};

const ACCA_COLOR = "#1c8a5a";

export default function Accasyllabus() {
  const navigate = useNavigate();
  const { levelId } = useParams();

  const { setSelectedCourse } = useExam();

  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedOptions, setSelectedOptions] = useState([]);

  const level = ACCA_LEVELS[levelId];

  /*
   * ==========================================================
   * LOAD EXAMS FROM DJANGO
   * ==========================================================
   */

  useEffect(() => {
    let mounted = true;

    const loadExams = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await getExams();

        if (!mounted) {
          return;
        }

        const data = response?.data;

        let examList = [];

        if (Array.isArray(data)) {
          examList = data;
        } else if (
          data &&
          Array.isArray(data.results)
        ) {
          examList = data.results;
        }

        setExams(examList);
      } catch (err) {
        console.error(
          "Failed to load ACCA exams:",
          err
        );

        if (mounted) {
          setError(
            "Could not load ACCA examinations."
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadExams();

    return () => {
      mounted = false;
    };
  }, []);

  /*
   * ==========================================================
   * EXAM MAP
   * ==========================================================
   */

  const examMap = useMemo(() => {
    const map = {};

    exams.forEach((exam) => {
      map[Number(exam.id)] = exam;
    });

    return map;
  }, [exams]);

  /*
   * ==========================================================
   * OPEN NORMAL PAPER
   * ==========================================================
   */

  const openPaper = (paper) => {
    const exam = examMap[paper.examId];

    if (!exam) {
      setError(
        `${paper.code} is currently unavailable.`
      );

      return;
    }

    setSelectedCourse(exam);

    navigate("/exam-criteria");
  };

  /*
   * ==========================================================
   * STRATEGIC PROFESSIONAL OPTIONS
   * ==========================================================
   */

  const toggleOption = (paper) => {
    setSelectedOptions((current) => {
      const exists = current.some(
        (item) => item.examId === paper.examId
      );

      if (exists) {
        return current.filter(
          (item) => item.examId !== paper.examId
        );
      }

      /*
       * Exactly two optional papers.
       */
      if (current.length >= 2) {
        return current;
      }

      return [...current, paper];
    });
  };

  /*
   * ==========================================================
   * CONTINUE WITH OPTIONS
   * ==========================================================
   */

  const continueWithOptions = () => {
    if (selectedOptions.length !== 2) {
      return;
    }

    /*
     * For now select the first chosen option and continue
     * through the existing exam flow.
     *
     * The two selected papers remain selected visually on
     * this page.
     */
    const firstExam =
      examMap[selectedOptions[0].examId];

    if (!firstExam) {
      setError(
        "One of the selected examinations is unavailable."
      );

      return;
    }

    setSelectedCourse(firstExam);

    navigate("/exam-criteria");
  };

  /*
   * ==========================================================
   * INVALID LEVEL
   * ==========================================================
   */

  if (!level) {
    return (
      <div className="oes-page-full">
        <div className="oes-home-shell d-flex flex-row overflow-hidden">

          <AppSidebar />

          <div className="flex-grow-1 p-3 p-md-4">

            <div className="alert alert-warning">
              ACCA level not found.
            </div>

            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate("/home")}
            >
              <i className="bi bi-arrow-left me-1" />
              Back to Courses
            </button>

          </div>
        </div>
      </div>
    );
  }

  /*
   * ==========================================================
   * PAPER CARD
   * ==========================================================
   */

  const PaperCard = ({
    paper,
    selected = false,
    selectable = false,
  }) => {
    const exam = examMap[paper.examId];

    const available =
      Boolean(exam) &&
      exam?.is_active !== false;

    const handleClick = () => {
      if (!available) {
        return;
      }

      if (selectable) {
        toggleOption(paper);
        return;
      }

      openPaper(paper);
    };

    return (
      <div className="col-6 col-sm-4 col-lg-3">
        <div
          className="course-tile"
          onClick={handleClick}
          style={{
            cursor: available
              ? "pointer"
              : "not-allowed",
            opacity: available ? 1 : 0.5,
            position: "relative",
            border: selected
              ? "2px solid #1c8a5a"
              : undefined,
          }}
        >

          {selectable && selected && (
            <div
              style={{
                position: "absolute",
                top: "8px",
                right: "8px",
              }}
            >
              <i
                className="bi bi-check-circle-fill"
                style={{
                  color: ACCA_COLOR,
                }}
              />
            </div>
          )}

          <div
            className="course-icon"
            style={{
              background: ACCA_COLOR,
            }}
          >
            <i className="bi bi-book" />
          </div>

          <div className="fw-semibold small">
            {paper.code}
          </div>

          <div className="text-muted small mt-1">
            {paper.name}
          </div>

        </div>
      </div>
    );
  };

  /*
   * ==========================================================
   * UI
   * ==========================================================
   */

  return (
    <div className="oes-page-full">
      <div className="oes-home-shell d-flex flex-row overflow-hidden">

        <AppSidebar />

        <div className="flex-grow-1 p-3 p-md-4">

          {/* HEADER */}

          <div className="d-flex justify-content-between align-items-center mb-3">

            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate("/home")}
            >
              <i className="bi bi-arrow-left me-1" />
              Back
            </button>

            <span
              className="badge"
              style={{
                background: ACCA_COLOR,
              }}
            >
              ACCA
            </span>

          </div>

          {/* PAGE TITLE */}

          <div className="mb-4">

            <h2 className="h5 mb-1">
              {level.title}
            </h2>

            <span className="badge oes-course-badge">
              {level.count}
            </span>

          </div>

          {/* ERROR */}

          {error && (
            <div className="alert alert-warning py-2 small">
              {error}
            </div>
          )}

          {/* LOADING */}

          {loading && (
            <p className="text-muted">
              Loading examinations...
            </p>
          )}

          {!loading && (
            <>
              {/* ==================================================
                  APPLIED KNOWLEDGE
                  ================================================== */}

              {levelId === "acca_knowledge" && (
                <>
                  <h5 className="fw-semibold mb-3">
                    1. Applied Knowledge
                  </h5>

                  <div className="row g-3">
                    {level.papers.map((paper) => (
                      <PaperCard
                        key={paper.examId}
                        paper={paper}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* ==================================================
                  APPLIED SKILLS
                  ================================================== */}

              {levelId === "acca_skills" && (
                <>
                  <h5 className="fw-semibold mb-3">
                    2. Applied Skills
                  </h5>

                  <div className="row g-3">
                    {level.papers.map((paper) => (
                      <PaperCard
                        key={paper.examId}
                        paper={paper}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* ==================================================
                  STRATEGIC PROFESSIONAL
                  ================================================== */}

              {levelId === "acca_professional" && (
                <>
                  <h5 className="fw-semibold mb-3">
                    3. Strategic Professional
                  </h5>

                  {/* MANDATORY */}

                  <div className="mb-4">

                    <h6 className="fw-semibold mb-3">
                      Essentials (Mandatory)
                    </h6>

                    <div className="row g-3">
                      {level.mandatory.map(
                        (paper) => (
                          <PaperCard
                            key={paper.examId}
                            paper={paper}
                          />
                        )
                      )}
                    </div>

                  </div>

                  {/* OPTIONS */}

                  <div>

                    <h6 className="fw-semibold mb-2">
                      Options (Choose any 2)
                    </h6>

                    <p className="text-muted small mb-3">
                      Select exactly two optional
                      papers.
                    </p>

                    <div className="row g-3">

                      {level.options.map(
                        (paper) => (
                          <PaperCard
                            key={paper.examId}
                            paper={paper}
                            selectable
                            selected={selectedOptions.some(
                              (item) =>
                                item.examId ===
                                paper.examId
                            )}
                          />
                        )
                      )}

                    </div>

                    <button
                      type="button"
                      className="btn btn-success mt-4"
                      disabled={
                        selectedOptions.length !== 2
                      }
                      onClick={
                        continueWithOptions
                      }
                    >
                      Continue with selected 2
                    </button>

                  </div>
                </>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
}